import { useCallback, useEffect, useState } from "react";

/**
 * MS66 — persist a small piece of UI state across page reloads via
 * localStorage.
 *
 * Use case: view-mode toggles, filter selections, "did the user
 * dismiss this banner" — anything UI-state-ish where we want the
 * page to remember what the user picked last time without putting
 * the preference into the database.
 *
 * Behaviour notes:
 *   - SSR-safe: on the server `window.localStorage` doesn't exist;
 *     the hook returns `initialValue` and never throws. The first
 *     client render re-reads from localStorage.
 *   - JSON-shaped: stored values are serialised via JSON.stringify /
 *     JSON.parse. Pass any JSON-serialisable type. Functions, dates
 *     and Maps will not survive the round-trip.
 *   - Validation: an optional `validate` predicate runs against the
 *     parsed value. When false, the hook falls back to `initialValue`
 *     and clears the stale entry. Useful when an enum's set of
 *     valid keys is narrowed — old persisted values shouldn't crash
 *     the UI.
 *   - Per-tab only: this is a single-tab persistence layer, no
 *     cross-tab storage-event subscription. Two tabs picking
 *     different filters won't sync; that's the simpler, less
 *     surprising default for these surfaces.
 *
 * Don't use this for: sensitive data, auth state, anything that
 * has to round-trip through the server. localStorage is unscoped
 * and easily inspected.
 */
export function usePersistedState<T>(
  storageKey: string,
  initialValue: T,
  options?: {
    /** Optional validator. When it returns false the stored value is
     *  discarded in favour of `initialValue`. */
    validate?: (parsed: unknown) => parsed is T;
  },
): [T, (next: T | ((prev: T) => T)) => void] {
  const validate = options?.validate;

  // Initial state: read once on mount. Use the lazy form so the
  // localStorage read only runs once per mount, not every render.
  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") return initialValue;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === null) return initialValue;
      const parsed = JSON.parse(raw) as unknown;
      if (validate && !validate(parsed)) {
        // Stale shape — clear so the next read doesn't keep falling
        // through the same validation failure.
        window.localStorage.removeItem(storageKey);
        return initialValue;
      }
      return parsed as T;
    } catch {
      // Parse / quota / disabled-storage all land here. Bail out
      // gracefully rather than crash the page.
      return initialValue;
    }
  });

  // Mirror writes back to localStorage. Best-effort; quota errors
  // and disabled-storage just no-op so the in-memory state remains
  // authoritative for the session.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      /* ignore */
    }
  }, [storageKey, value]);

  const setter = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) =>
        typeof next === "function" ? (next as (p: T) => T)(prev) : next,
      );
    },
    [],
  );

  return [value, setter];
}
